import { fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismNumberInput, PrismRadioGroup } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  NUMBER_INPUT_MAX_VALUE,
  OVERRIDE_TYPES,
  type OverrideTypes,
} from 'lumen/utils/constants/pricing-assistant-constants';

import type EditOverrideModal from './edit-override-modal';

type ValidationError = {
  errorMessage: string;
  hasErrors: boolean;
  showErrors: boolean;
};

interface HotelPricingAssistantTableModalsEditOverrideModalPriceOverrideFormSignature {
  Args: {
    exactPriceOverrideNewPrice: number | null;
    exactPriceOverrideNewPriceError: ValidationError;
    exactPriceOverrideNewPriceWarning: {
      hasWarnings: boolean;
      warningMessage: string;
    };
    exactPricePlaceholder?: string;
    onOverrideEditTypeChange: (event: Event) => void;
    onValueChange: (typeof EditOverrideModal)['prototype']['onValueChange'];
    priceRangeMaxPlaceholder?: string;
    priceRangeMinPlaceholder?: string;
    priceRangeOverrideErrors: {
      priceRangeOverrideMaxPriceError: ValidationError;
      priceRangeOverrideMinPriceError: ValidationError;
    };
    priceRangeOverrideMaxPrice: number | null;
    priceRangeOverrideMinPrice: number | null;
    selectedOverrideEditType: OverrideTypes;
  };
}

class HotelPricingAssistantTableModalsEditOverrideModalPriceOverrideForm extends Component<HotelPricingAssistantTableModalsEditOverrideModalPriceOverrideFormSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  <template>
    <PrismRadioGroup
      @isHorizontal={{true}}
      @name="override-types"
      @onChange={{@onOverrideEditTypeChange}}
      @value={{@selectedOverrideEditType}}
      as |RadioGroup|
    >
      <RadioGroup.input
        @value={{OVERRIDE_TYPES.PRICE_RANGE}}
        data-test-id="price-range-override-radio-button"
      >
        {{t "pricing_assistant.override_edit_type.range"}}
      </RadioGroup.input>

      <RadioGroup.input
        @value={{OVERRIDE_TYPES.EXACT_PRICE}}
        data-test-id="exact-price-override-radio-button"
      >
        {{t "pricing_assistant.override_edit_type.current_price"}}
      </RadioGroup.input>
    </PrismRadioGroup>

    {{#if (eq @selectedOverrideEditType OVERRIDE_TYPES.PRICE_RANGE)}}
      <div
        class="override-modal__price-container items-start justify-between gap-400"
        data-test-id="price-range-override-container"
      >
        <PrismNumberInput
          @errorMessage={{@priceRangeOverrideErrors.priceRangeOverrideMinPriceError.errorMessage}}
          @hasErrors={{@priceRangeOverrideErrors.priceRangeOverrideMinPriceError.showErrors}}
          @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
          @isTouched={{true}}
          @label={{t "pricing_assistant.label.min_price"}}
          @max={{NUMBER_INPUT_MAX_VALUE}}
          @min={{0}}
          @onValueChange={{fn (@onValueChange "min_range")}}
          @placeholder={{@priceRangeMinPlaceholder}}
          @removePadding={{true}}
          @value={{@priceRangeOverrideMinPrice}}
          data-test-id="price-range-override-min-price-input"
        />

        <div class="flex items-center pt-600">-</div>

        <PrismNumberInput
          @errorMessage={{@priceRangeOverrideErrors.priceRangeOverrideMaxPriceError.errorMessage}}
          @hasErrors={{@priceRangeOverrideErrors.priceRangeOverrideMaxPriceError.showErrors}}
          @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
          @isTouched={{true}}
          @label={{t "pricing_assistant.label.max_price"}}
          @max={{NUMBER_INPUT_MAX_VALUE}}
          @min={{0}}
          @onValueChange={{fn (@onValueChange "max_range")}}
          @placeholder={{@priceRangeMaxPlaceholder}}
          @removePadding={{true}}
          @value={{@priceRangeOverrideMaxPrice}}
          data-test-id="price-range-override-max-price-input"
        />

      </div>
    {{/if}}

    {{#if (eq @selectedOverrideEditType OVERRIDE_TYPES.EXACT_PRICE)}}
      <div
        class="override-modal__price-container"
        data-test-id="exact-price-override-new-price-container"
      >
        <PrismNumberInput
          @errorMessage={{@exactPriceOverrideNewPriceError.errorMessage}}
          @hasErrors={{@exactPriceOverrideNewPriceError.showErrors}}
          @hasWarnings={{@exactPriceOverrideNewPriceWarning.hasWarnings}}
          @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
          @isTouched={{true}}
          @label={{t "pricing_assistant.label.new_price"}}
          @max={{NUMBER_INPUT_MAX_VALUE}}
          @min={{0}}
          @onValueChange={{fn (@onValueChange "exact")}}
          @placeholder={{@exactPricePlaceholder}}
          @removePadding={{true}}
          @value={{@exactPriceOverrideNewPrice}}
          @warningMessage={{@exactPriceOverrideNewPriceWarning.warningMessage}}
          data-test-id="exact-price-override-new-price-input"
        />
      </div>
    {{/if}}
  </template>
}

export default HotelPricingAssistantTableModalsEditOverrideModalPriceOverrideForm;
